import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import CardColumn from '../components/CardColumn';
import ParticipantSidebar from '../components/ParticipantSidebar';
import ActionSection from '../components/ActionSection';
import HealthCheckMain from '../components/HealthCheckMain';
import { useAuth } from '../AuthContext';
import './RetroBoard.css';

const RETRO_CONFIGS = {
  'start-stop-continue': {
    label: 'Start · Stop · Continue',
    columns: [
      { category: 'start',    title: 'Start',    color: '#10b981' },
      { category: 'stop',     title: 'Stop',     color: '#ef4444' },
      { category: 'continue', title: 'Continue', color: '#3b82f6' },
    ],
  },
  'mad-sad-glad': {
    label: 'Mad · Sad · Glad',
    columns: [
      { category: 'mad',  title: 'Mad',  color: '#ef4444' },
      { category: 'sad',  title: 'Sad',  color: '#3b82f6' },
      { category: 'glad', title: 'Glad', color: '#10b981' },
    ],
  },
  '4ls': {
    label: 'Liked · Learned · Lacked · Longed For',
    columns: [
      { category: 'liked',      title: 'Liked',      color: '#10b981' },
      { category: 'learned',    title: 'Learned',    color: '#3b82f6' },
      { category: 'lacked',     title: 'Lacked',     color: '#ef4444' },
      { category: 'longed-for', title: 'Longed For', color: '#f59e0b' },
    ],
  },
  'health-check': {
    label: 'Health Check',
    columns: [],
    special: 'health-check',
  },
};

export default function RetroBoard() {
  const { sessionId } = useParams();
  const { user } = useAuth();
  const [cards, setCards] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [actions, setActions] = useState([]);
  const [sessionName, setSessionName] = useState('');
  const [sessionType, setSessionType] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [nameCommitted, setNameCommitted] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const pollRef = useRef(null);
  const joinedRef = useRef(false);

  async function fetchSession() {
    try {
      const res = await fetch(`/api/sessions/${sessionId}`);
      if (!res.ok) throw new Error('Session not found');
      const data = await res.json();
      setSessionName(data.name ?? '');
      setSessionType(data.type ?? '');
      setCards(data.cards ?? []);
      setParticipants(data.participants ?? []);
      setActions(data.actions ?? []);
    } catch (err) {
      setError(err.message);
    }
  }

  useEffect(() => {
    fetchSession();
    pollRef.current = setInterval(fetchSession, 3000);
    return () => clearInterval(pollRef.current);
  }, [sessionId]);

  useEffect(() => {
    if (user && !joinedRef.current) {
      joinedRef.current = true;
      setAuthorName(user.name);
      setNameCommitted(true);
      fetch(`/api/sessions/${sessionId}/participants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: user.name }),
      });
    }
  }, [user, sessionId]);

  async function commitName(e) {
    e.preventDefault();
    if (!authorName.trim()) return;
    await fetch(`/api/sessions/${sessionId}/participants`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: authorName.trim() }),
    });
    setNameCommitted(true);
    fetchSession();
  }

  async function copyLink() {
    await navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  if (error) {
    return (
      <div className="board-error">
        <p>{error}</p>
        <Link to="/">Back to home</Link>
      </div>
    );
  }

  const config = RETRO_CONFIGS[sessionType];
  const columns = config?.columns ?? [];
  const retroLabel = config?.label ?? '';

  return (
    <div className="board">
      <header className="board-header">
        <Link to="/" className="board-back">← retrospekt</Link>
        <div className="board-header-center">
          <h1 className="board-title">{sessionName || 'Retrospective'}</h1>
          <span className="board-subtitle">{retroLabel}</span>
        </div>
        <div className="board-header-right">
          <button className="share-btn" onClick={copyLink}>
            {copied ? '✓ Copied!' : 'Copy link'}
          </button>
          <div className="board-name-area">
            {nameCommitted ? (
              <div className="board-name-display">
                {user?.picture
                  ? <img src={user.picture} alt="" className="board-name-avatar-img" />
                  : <span className="board-name-avatar">{authorName[0].toUpperCase()}</span>
                }
                <span className="board-name-text">{authorName}</span>
                {!user && (
                  <button className="board-name-change" onClick={() => setNameCommitted(false)}>Change</button>
                )}
              </div>
            ) : (
              <form className="board-name-form" onSubmit={commitName}>
                <input
                  className="board-name-input"
                  type="text"
                  placeholder="Your name"
                  value={authorName}
                  onChange={(e) => setAuthorName(e.target.value)}
                  autoFocus
                />
                <button className="board-name-btn" type="submit" disabled={!authorName.trim()}>
                  Join
                </button>
              </form>
            )}
          </div>
        </div>
      </header>

      <div className="board-body">
        <ParticipantSidebar
          participants={participants}
          cards={cards}
          currentUser={nameCommitted ? authorName : ''}
        />
        <div className="board-main">
          {config?.special === 'health-check' ? (
            <HealthCheckMain
              sessionId={sessionId}
              authorName={nameCommitted ? authorName : ''}
              cards={cards}
            />
          ) : (
            <main className="board-columns" style={{ '--col-count': columns.length }}>
              {columns.map(({ category, title, color }) => (
                <CardColumn
                  key={category}
                  sessionId={sessionId}
                  category={category}
                  title={title}
                  color={color}
                  authorName={nameCommitted ? authorName : ''}
                  cards={cards.filter((c) => c.category === category)}
                />
              ))}
            </main>
          )}
          <ActionSection
            sessionId={sessionId}
            actions={actions}
            participants={participants}
            authorName={nameCommitted ? authorName : ''}
          />
        </div>
      </div>
    </div>
  );
}
